import {
  ActionIcon,
  CopyButton,
  Group,
  Stack,
  Switch,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { IconCheck, IconCopy, IconPlus, IconRefresh, IconTrash } from '@tabler/icons-react';
import { WebBotProviderData } from '@/API';

interface WebBotSecurityTabProps {
  form: UseFormReturnType<WebBotProviderData>;
}

export function WebBotSecurityTab({ form }: WebBotSecurityTabProps) {
  const isSecurityEnabled = form.values.security?.enableSecurity ?? false;

  const generateSecretKey = () => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    const secret = Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
    form.setFieldValue('security.secretKey', secret);
  };

  return (
    <Stack gap="md">
      <Switch
        label="啟用安全驗證 (JWT Token)"
        description="開啟後，Widget 將不再接受明文的 UserId，必須使用由您的伺服器簽發的 JWT Token 作為授權"
        checked={isSecurityEnabled}
        {...form.getInputProps('security.enableSecurity', { type: 'checkbox' })}
      />
      {isSecurityEnabled && (
        <TextInput
          label="SecretKey"
          description="用於簽發 JWT 的密鑰 (HMAC-SHA256)"
          rightSectionWidth={80}
          rightSection={
            <Group gap={4}>
              <CopyButton value={form.values.security?.secretKey || ''} timeout={2000}>
                {({ copied, copy }) => (
                  <Tooltip label={copied ? '已複製' : '複製密鑰'} withArrow position="right">
                    <ActionIcon color={copied ? 'teal' : 'gray'} onClick={copy}>
                      {copied ? <IconCheck size="1rem" /> : <IconCopy size="1rem" />}
                    </ActionIcon>
                  </Tooltip>
                )}
              </CopyButton>
              <Tooltip label="重新產生" withArrow position="right">
                <ActionIcon onClick={generateSecretKey}>
                  <IconRefresh size="1rem" />
                </ActionIcon>
              </Tooltip>
            </Group>
          }
          {...form.getInputProps('security.secretKey')}
        />
      )}
      <Stack gap="xs" mt="md">
        <Group justify="space-between">
          <div>
            <span style={{ fontSize: '14px', fontWeight: 500 }}>允許嵌入的網域</span>
            <div style={{ fontSize: '12px', color: 'gray' }}>
              若未設定任何網域，則允許所有網站嵌入；若有設定，則僅允許列表中的網站使用。例如: https://example.com
            </div>
          </div>
          <ActionIcon variant="light" color="blue" onClick={() => form.insertListItem('security.allowedDomains', '')}>
            <IconPlus size="1rem" />
          </ActionIcon>
        </Group>
        {form.values.security?.allowedDomains?.map((_, index) => (
          <Group key={index} gap="xs">
            <TextInput
              placeholder="https://example.com"
              style={{ flex: 1 }}
              {...form.getInputProps(`security.allowedDomains.${index}`)}
            />
            <ActionIcon color="red" onClick={() => form.removeListItem('security.allowedDomains', index)}>
              <IconTrash size="1rem" />
            </ActionIcon>
          </Group>
        ))}
      </Stack>
    </Stack>
  );
}
